import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { SignupLoginOption } from 'src/app/models/signup.model';
import { CountryISO } from 'src/app/utilities/country-iso';

export const EMAIL_VALIDATION_REGEX = '^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,50}$';
@Injectable()
export class FormService {
  public login = false;

  constructor(public translate: TranslateService) {}

  public validate(controls: { [key: string]: AbstractControl }, login = false): string[] {
    const errorsMap: Map<string, string[]> = new Map<string, string[]>();

    this.login = login;
    Object.keys(controls).forEach(key => {
      if (controls[key].errors != null) {
        this.doChecks(errorsMap, controls[key], key);
      }
    });
    return this.errorsMapToArrayString(errorsMap);
  }

  public getErrorsByControlNames(form: FormGroup, controlNames: string[], skipUntouched = true): string[] {
    const errorsMap = new Map<string, string[]>();
    controlNames
      .filter(cn => cn)
      .forEach(controlName => {
        const control = form.get(controlName);
        if (control) {
          if (!control.touched && skipUntouched) {
            return [];
          }
          if (control.errors != null) {
            this.doChecks(errorsMap, control, controlName);
          }
        }
      });
    return this.errorsMapToArrayString(errorsMap);
  }

  public clearAllErrors(controls: { [key: string]: AbstractControl }): void {
    Object.keys(controls).forEach(key => {
      if (controls[key].errors != null) {
        controls[key].setErrors(null);
        controls[key].markAsUntouched();
        controls[key].updateValueAndValidity();
      }
    });
  }

  public addCustomError(form: FormGroup, controlName: string, errorMsg: string): void {
    const control = form.get(controlName);
    if (control) {
      const error = control.errors ? control.errors : {};
      error.custom = errorMsg;
      control.setErrors(error);
    }
  }

  public transformKey(key: string) {
    return key
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/_/g, ' ')
      .replace(/(?:^|\s)\S/g, l => l.toUpperCase());
  }

  public doChecks(errorsMap: Map<string, string[]>, control: AbstractControl, key: string) {
    for (const k in control?.errors) {
      /* istanbul ignore else reason: not required*/
      if (k) this.pushErrorToMap(errorsMap, key, this.setErrorMessages(control.errors, key, k));
    }
  }

  setErrorMessages(errors: ValidationErrors, key: string, property: string): string {
    let errorMsg = '';
    switch (property) {
      case 'required':
        errorMsg = this.translate.instant('ValidationMessages.checkRequired', { field: this.translateKey(key) });
        break;
      case 'maxlength':
        errorMsg = this.populateMaxMinLengthError(errors, key, property);
        break;
      case 'minlength':
        errorMsg = this.populateMaxMinLengthError(errors, key, property);
        break;
      case 'referralCode':
        errorMsg = `${this.translate.instant('ValidationMessages.checkReferralCode')}`;
        break;
      case 'date':
        errorMsg = this.populateCheckNumberError(key, 'checkDate');
        break;
      case 'space':
        errorMsg = this.populateCheckNumberError(key, 'checkOnlySpace');
        break;
      case 'emailInvalid':
        errorMsg = this.populateCheckNumberError(key, 'checkEmail');
        break;
      case 'max':
        errorMsg = this.populateMaxMinError(errors, key, property);
        break;
      case 'min':
        errorMsg = this.populateMaxMinError(errors, key, property);
        break;
      case 'number':
        errorMsg = this.populateCheckNumberError(key, 'checkNumber');
        break;
      case 'strong':
        errorMsg = `${this.translate.instant('ValidationMessages.checkStrength')}`;
        break;
      case 'pattern':
        {
          if (this.login) errorMsg = `${this.translate.instant('ValidationMessages.checkPatternLogin')}`;
          else errorMsg = this.populateCheckNumberError(key, 'checkPattern');
        }
        break;
      case 'duration':
        errorMsg = `${this.translate.instant('ValidationMessages.checkDurationA')} ${this.translateKey(key)} ${this.translate.instant(
          'ValidationMessages.checkDurationB'
        )}`;
        break;
      case 'phoneNumber':
        errorMsg = `${this.translate.instant('ValidationMessages.invalidMobileNumber')}`;
        break;
      case 'custom':
        errorMsg = errors.custom;
        break;
      default:
        break;
    }
    return errorMsg;
  }

  populateMaxMinError(errors: ValidationErrors, key: string, type: string) {
    const msg = type === 'max' ? 'ValidationMessages.checkNumberMax' : 'ValidationMessages.checkNumberMin';
    return this.translate.instant(msg, {
      field: this.translateKey(key),
      // eslint-disable-next-line id-blacklist
      number: type === 'max' ? errors.max.max : errors.min.min,
    });
  }

  populateMaxMinLengthError(errors: ValidationErrors, key: string, type: string) {
    const msg = type === 'maxlength' ? 'ValidationMessages.checkLength' : 'ValidationMessages.checkLengthMinLength';
    return this.translate.instant(msg, {
      field: this.translateKey(key),
      // eslint-disable-next-line id-blacklist
      number: type === 'maxlength' ? errors.maxlength.requiredLength : errors.minlength.requiredLength,
    });
  }

  populateCheckNumberError(key: string, type: string) {
    const msg = `ValidationMessages.${type}`;
    return this.translate.instant(msg, { field: this.translateKey(key) });
  }

  setErrorInMap(errorsMap, key, errorKey) {
    const error = `ValidationMessage.${errorKey}`;
    this.pushErrorToMap(errorsMap, key, `${this.translate.instant(error)}`);
  }

  public removeId(prettyKey: string) {
    if (prettyKey.endsWith(' Id')) {
      return prettyKey.substring(0, prettyKey.length - 3);
    }
    return prettyKey;
  }

  translateKey(key: string): string {
    if (key === 'mobile') {
      return this.prettyKey(this.translate.instant('ValidationMessages.mobile'));
    } else if (key === 'firstName') {
      return this.prettyKey(this.translate.instant('ValidationMessages.firstName'));
    } else if (key === 'lastName') {
      return this.prettyKey(this.translate.instant('ValidationMessages.lastName'));
    } else if (key === 'countryCode') {
      return this.prettyKey(this.translate.instant('ValidationMessages.countryCode'));
    } else if (key === 'email') {
      return this.prettyKey(this.translate.instant('ValidationMessages.email'));
    } else if (key === 'programCode') {
      return this.prettyKey(this.translate.instant('ValidationMessages.programCode'));
    } else if (key === 'inputAnswer' || key === 'answer') {
      return this.prettyKey(this.translate.instant('ValidationMessages.answer'));
    } else {
      return this.prettyKey(key);
    }
  }

  prettyKey(key: string): string {
    const prettyKey = this.transformKey(key);
    return this.removeId(prettyKey);
  }

  pushErrorToMap(errorsMap: Map<string, string[]>, key: string, error: string): void {
    if (!errorsMap.has(key)) {
      errorsMap.set(key, []);
    }
    const currentErrors = errorsMap.get(key);
    currentErrors.push(error);
    errorsMap.set(key, currentErrors);
  }

  errorsMapToArrayString(errorsMap: Map<string, string[]>): string[] {
    const errorMessages = [];
    // combine all error messages from all keys
    Array.from(errorsMap.values())
      .filter(e => e.length)
      .forEach(errs => errs.forEach(e => errorMessages.push(e)));
    return errorMessages;
  }

  public validateForm(option: SignupLoginOption, loginSignupForm: FormGroup) {
    if (option === SignupLoginOption.mobile) {
      loginSignupForm.controls.email.setValidators(null);
      loginSignupForm.controls.email.updateValueAndValidity();
    } else {
      loginSignupForm.controls.mobile.setValidators(null);
      loginSignupForm.controls.mobile.updateValueAndValidity();
    }
    const errors = this.validate(loginSignupForm.controls, true);
    return !errors.length;
  }
}
